import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Main.css';

import agentIcon from '../../assets/agent3.png';
import llmIcon from '../../assets/model_ai.png';
import mcpIcon from '../../assets/mcp.png';
import networkIcon from '../../assets/gateway.png';
import apiIcon from '../../assets/api2.png';
import identityIcon from '../../assets/thumb.png';
import accessIcon from '../../assets/access.png';
import discoveryIcon from '../../assets/magnifyingglass.png';
import chatIcon from '../../assets/chat.png';

import owaspIcon from '../../assets/owasp.png';
import mitreIcon from '../../assets/mitreatlas.png';
import isoIcon from '../../assets/iso42001.png';
import soc2Icon from '../../assets/soc2.png';
import nistIcon from '../../assets/nisrmf.png';


import runtimeImg from '../../assets/home/shield_still.png';
import purpleteamImg from '../../assets/home/purpleshield.png';
import blueShieldImg from '../../assets/blueshield.png';
import redBgImg from '../../assets/red.png';
import redGif from '../../assets/movie/red.gif';
import blueBgImg from '../../assets/blue.png';
import blueGif from '../../assets/movie/blue.gif';
import howBgImg from '../../assets/how_it_works.png';
import howGif from '../../assets/movie/how_it_works.gif';
import workspaceImg from '../../assets/home/workspace_icon.png';
import logoRotate from '../../assets/movie/logo_rotate_large_v2.gif';
import visibilityChart from '../../assets/visibility.png';

import workspaceVisibilityImg from '../../assets/home/workspace_visibility.png';
import aiGovernancePolicyImg from '../../assets/home/ai_governance_policy.png';
import isoChartImg from '../../assets/home/iso_chart.png';
import soc2ChartImg from '../../assets/home/soc2_chart.png';
import nistChartImg from '../../assets/home/nist_chart.png';
import openclawWarningImg from '../../assets/home/openclaw_warning.png';

import gptIcon from '../../assets/openaicircle_dark.png';
import githubIcon from '../../assets/github.png';
import huggingFaceIcon from '../../assets/huggingfacecircle.png';
import metaIcon from '../../assets/meta.png';
import claudeIcon from '../../assets/anthropic.png';
import geminiIcon from '../../assets/geminicircle.png';
import mistralIcon from '../../assets/mistral.png';
import deepseekIcon from '../../assets/deepseek.png';
import ollamaIcon from '../../assets/ollama.png';
import bedrockIcon from '../../assets/bedrock.png';
import vertexIcon from '../../assets/vertexai.png';
import cohereIcon from '../../assets/cohere.png';
import nvidiaIcon from '../../assets/nividia.png';
import langchainIcon from '../../assets/langchain.png';
import pytorchIcon from '../../assets/pytorch.png';
import tensorflowIcon from '../../assets/tensorflow.png';
import copilotIcon from '../../assets/githubcopilot.png';
import kerasIcon from '../../assets/keras.png';
import pineconeIcon from '../../assets/pinecone.png';
import aiAppsIcon from '../../assets/aiapps.png';
import databaseBlueIcon from '../../assets/database_blue.png';
import crewaiIcon from '../../assets/crewai.png';
import qwenIcon from '../../assets/qwen.png';
import moltbotIcon from '../../assets/moltbot.png';

const tickerItems = [
  { text: 'Agent', icon: agentIcon, invert: true },
  { text: 'LLM', icon: llmIcon, invert: true },
  { text: 'MCP', icon: mcpIcon, invert: true },
  { text: 'Network', icon: networkIcon, invert: true },
  { text: 'API Security', icon: apiIcon, invert: true, small: true },
  { text: 'Identity', icon: identityIcon, invert: true },
  { text: 'Access', icon: accessIcon },
  { text: 'Discovery', icon: discoveryIcon, invert: true },
  { text: 'ChatBot', icon: chatIcon, invert: true },
];

const Main = () => {
  const [videoOpen, setVideoOpen] = useState(false);
  const logoRef = useRef(null);
  const headersRef = useRef(null);
  const dividerRef = useRef(null);
  const sectionDarkRef = useRef(null);
  const ptCardsRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const el = headersRef.current;
    if (!el) return;
    const boxes = el.querySelectorAll('.gw-header-box');
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        boxes.forEach(box => box.classList.add('gw-header-box-animate'));
      } else {
        boxes.forEach(box => box.classList.remove('gw-header-box-animate'));
      }
    }, { threshold: 0.5 });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const section = sectionDarkRef.current;
    const el = dividerRef.current;
    if (!section || !el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        el.classList.add('divider-animate');
      } else {
        el.classList.remove('divider-animate');
      }
    }, { threshold: 0.3 });
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const section = sectionDarkRef.current;
    const cards = ptCardsRef.current;
    if (!section || !cards) return;
    let hasFlashed = false;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !hasFlashed) {
        hasFlashed = true;
        const cardEls = cards.querySelectorAll('.pt-card');
        cardEls.forEach(c => c.classList.add('pt-card-flash'));
        setTimeout(() => {
          cardEls.forEach(c => c.classList.remove('pt-card-flash'));
        }, 1000);
      }
    }, { threshold: 0.3 });
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (logoRef.current) {
        const src = logoRef.current.src;
        logoRef.current.src = '';
        logoRef.current.src = src;
      }
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(true);
  const [scrollOnDark, setScrollOnDark] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight;
      const winHeight = window.innerHeight;
      setCanScrollUp(scrollTop > 10);
      setCanScrollDown(scrollTop + winHeight < docHeight - 10);

      // Dark/light detection at the button position (bottom-right)
      const btnY = winHeight - 50;
      const dark = document.querySelector('.section-dark');
      const hero = document.querySelector('.main-hero');
      let onDark = false;
      if (hero) {
        const r = hero.getBoundingClientRect();
        if (r.top < btnY && r.bottom > btnY) onDark = true;
      }
      if (dark) {
        const r = dark.getBoundingClientRect();
        if (r.top < btnY && r.bottom > btnY) onDark = true;
      }
      setScrollOnDark(onDark);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (direction) => {
    const sectionSelectors = ['.main-hero', '.section-white', '.section-dark', '.section-workspace', '.site-footer'];
    const nav = document.querySelector('.navigation');
    const navHeight = nav ? nav.offsetHeight : 0;
    const currentScroll = window.scrollY;

    // Build ordered list of scroll positions for comparison
    const targets = sectionSelectors.map(sel => {
      const el = document.querySelector(sel);
      if (!el) return null;
      const elTop = el.getBoundingClientRect().top + window.scrollY;
      return { elTop, sel };
    }).filter(Boolean);

    if (direction === 'down') {
      const next = targets.find(t => t.elTop > currentScroll + navHeight + 10);
      if (next) {
        if (next.sel === '.section-white') {
          document.getElementById('runtime-shield').scrollIntoView({ behavior: 'smooth' });
        } else if (next.sel === '.section-workspace') {
          window.scrollTo({ top: next.elTop - 10, behavior: 'smooth' });
        } else {
          window.scrollTo({ top: next.elTop, behavior: 'smooth' });
        }
      }
    } else {
      const prev = [...targets].reverse().find(t => t.elTop < currentScroll - 10);
      if (prev) {
        if (prev.sel === '.section-white') {
          document.getElementById('runtime-shield').scrollIntoView({ behavior: 'smooth' });
        } else if (prev.sel === '.section-workspace') {
          window.scrollTo({ top: prev.elTop - 10, behavior: 'smooth' });
        } else {
          window.scrollTo({ top: prev.elTop, behavior: 'smooth' });
        }
      }
    }
  };

  return (
    <>
      <div className="scroll-float-wrap">
        {canScrollUp && (
          <button className={`scroll-float-bubble ${scrollOnDark ? 'scroll-bubble-dark' : 'scroll-bubble-light'}`} onClick={() => scrollToSection('up')} aria-label="Scroll up">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none"><polygon points="12,6 4,16 20,16" fill="currentColor"/></svg>
          </button>
        )}
        {canScrollDown && (
          <button className={`scroll-float-bubble ${scrollOnDark ? 'scroll-bubble-dark' : 'scroll-bubble-light'}`} onClick={() => scrollToSection('down')} aria-label="Scroll down">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none"><polygon points="12,18 4,8 20,8" fill="currentColor"/></svg>
          </button>
        )}
      </div>
      <section className="main-hero">
        <div className="hero-gif-fade-left"></div>
        <div className="hero-gif-fade-right"></div>
        <div className="hero-two-col">
          <div className="hero-col-left">
            <div className="hero-pre-title">Agentic AI Security Platform</div>
            <div className="hero-sub-title">Unifying Offensive and Defensive <br />AI Security</div>
            <p className="hero-description">Bltz AI unifies visibility, prevention, detection, and automated remediation, closing the loop on AI risk by fixing issues instead of just exposing them.</p>
            <button className="hero-demo-btn hero-demo-btn-inline" onClick={() => { navigate('/book-demo'); window.scrollTo(0, 0); }}>
              Get a Live Demo
              <span className="hero-demo-arrow">→</span>
            </button>
            <div className="hero-video-thumb" onClick={() => setVideoOpen(true)}>
              <img ref={logoRef} src={logoRotate} alt="Watch intro video" className="hero-video-thumb-logo" />
              <div className="hero-video-thumb-bottom">
                <div className="hero-video-play">
                  <svg viewBox="0 0 24 24" fill="#ffffff" width="14" height="14"><polygon points="7,4 19,12 7,20" /></svg>
                </div>
                <span className="hero-video-thumb-label">See Bltz in Action</span>
              </div>
            </div>
          </div>
          <div className="hero-connector-wrap">
            <div className="hero-connector-dot"></div>
            <svg className="hero-connector-svg" viewBox="0 0 300 400">
              <defs>
                <linearGradient id="lineGradient" x1="0" y1="0" x2="300" y2="0" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="rgb(180,150,255)" stopOpacity="0" />
                  <stop offset="15%" stopColor="rgb(180,150,255)" stopOpacity="0.15" />
                  <stop offset="40%" stopColor="rgb(180,150,255)" stopOpacity="0.6" />
                  <stop offset="100%" stopColor="rgb(180,150,255)" stopOpacity="0.6" />
                </linearGradient>
              </defs>
              <path className="hero-connector-line" d="M0,200 C30,200 40,45 300,45" />
              <path className="hero-connector-line hero-connector-line-2" d="M0,200 C80,200 260,200 340,200" />
              <path className="hero-connector-line hero-connector-line-3" d="M0,200 C30,200 40,355 300,355" />
            </svg>
          </div>
          <div className="hero-col-right">
            <div className="hero-glass-cards">
              <div className="hero-glass-card" style={{ cursor: 'pointer' }} onClick={() => document.getElementById('runtime-shield').scrollIntoView({ behavior: 'smooth' })}>
                <img src={runtimeImg} alt="Runtime Shield" className="hero-glass-card-img" />
                <div className="hero-glass-card-text">
                  <span className="hero-glass-card-link">Runtime Shield <span style={{fontSize: '10px', marginLeft: '6px'}}>&#9654;</span></span>
                  <span className="hero-glass-card-title"> Runtime AI Visibility & Protection</span>
                </div>
              </div>
              <div className="hero-glass-card" style={{ cursor: 'pointer' }} onClick={() => {
                const section = document.querySelector('.section-dark');
                if (section) {
                  const nav = document.querySelector('.navigation');
                  const navHeight = nav ? nav.offsetHeight : 0;
                  const top = section.getBoundingClientRect().top + window.scrollY;
                  window.scrollTo({ top, behavior: 'smooth' });
                }
              }}>
                <img src={purpleteamImg} alt="Purple Team" className="hero-glass-card-img" />
                <div className="hero-glass-card-text">
                  <span className="hero-glass-card-link">Purple Team <span style={{fontSize: '10px', marginLeft: '6px'}}>&#9654;</span></span>
                  <span className="hero-glass-card-title"> Agentic Red & Blue Teaming, Automated</span>
                </div>
              </div>
              <div className="hero-glass-card" style={{ cursor: 'pointer' }} onClick={() => {
                const section = document.getElementById('workspace');
                if (section) {
                  const top = section.getBoundingClientRect().top + window.scrollY - 10;
                  window.scrollTo({ top, behavior: 'smooth' });
                }
              }}>
                <img src={workspaceImg} alt="Workspace" className="hero-glass-card-img" />
                <div className="hero-glass-card-text">
                  <span className="hero-glass-card-link">AI Workspace <span style={{fontSize: '10px', marginLeft: '6px'}}>&#9654;</span></span>
                  <span className="hero-glass-card-title">Automated AI Governance & Control</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="compliance-icons">
          <span className="tooltip-wrapper" data-tooltip="OWASP LLM TOP 10">
            <img src={owaspIcon} alt="OWASP" className="compliance-icon" />
          </span>
          <span className="tooltip-wrapper" data-tooltip="MITRE ATLAS">
            <img src={mitreIcon} alt="MITRE" className="compliance-icon" />
          </span>
          <span className="tooltip-wrapper" data-tooltip="ISO 42001">
            <img src={isoIcon} alt="ISO" className="compliance-icon" />
          </span>
          <span className="tooltip-wrapper" data-tooltip="SOC2">
            <img src={soc2Icon} alt="SOC2" className="compliance-icon grayscale" />
          </span>
          <span className="tooltip-wrapper" data-tooltip="NIST RMF">
            <img src={nistIcon} alt="NIST" className="compliance-icon" />
          </span>
        </div>
        <p className="compliance-subtitle">Supports above industry framework reporting</p>
      </section>
      {videoOpen && (
        <div className="hero-video-modal" onClick={() => setVideoOpen(false)}>
          <div className="hero-video-modal-content" onClick={e => e.stopPropagation()}>
            <button className="hero-video-modal-close" onClick={() => setVideoOpen(false)}>✕</button>
            <iframe
              src="https://www.youtube.com/embed/jDoTa0Hbu_w?autoplay=1"
              title="Bltz AI Intro"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="hero-video-iframe"
            />
          </div>
        </div>
      )}
      <section className="section-white">
        <div className="ticker-container">
          <div className="ticker-track">
            {[...tickerItems, ...tickerItems, ...tickerItems].map((item, index) => (
              <div key={index} className="ticker-item">
                {item.icon && <img src={item.icon} alt={item.text} className={`ticker-icon ${item.invert ? 'invert' : ''} ${item.small ? 'small' : ''}`} />}
                <span className="ticker-text">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
        <h2 id="runtime-shield" className="section-title"> Runtime Visibility and Protection</h2>
        <p className="section-description">Bltz enables complete visibility and protection for AI in production, discovering and monitoring all AI traffic at runtime while stopping prompt attacks, data leaks, and unsafe behavior with built-in AI governance and self-healing remediation.</p>
        <button className="section-learn-more-btn" onClick={() => navigate('/runtime-shield')}>Learn More</button>

        {/* Gateway Infographic */}
        <div className="gw-infographic">
          {/* Top header boxes */}
          <div className="gw-top-headers" ref={headersRef}>
            <div className="gw-header-box" onClick={() => navigate('/runtime-shield')}>One-Stop<br/>Integration</div>
            <div className="gw-header-box" onClick={() => navigate('/runtime-shield')}>Discover<br/>Shadow AI</div>
            <div className="gw-header-box" onClick={() => navigate('/runtime-shield')}>Prevent<br/>Unauthorized Access</div>
            <div className="gw-header-box" onClick={() => navigate('/runtime-shield')}>Detect & Remediate<br/>Data Loss</div>
          </div>

          <div className="gw-main-row">
            {/* SVG connecting lines */}
            <svg className="gw-connecting-lines" viewBox="0 0 1060 620" preserveAspectRatio="none">
              {/* Left lines - from left icon centers to gateway left edge */}
              {/* <path d="M140,-80 C250,-40 320,60 383,80" className="gw-line" style={{animationDelay: '0.9s'}} /> */}
              <path d="M0,10 C190,38 260,120 323,135" className="gw-line" />
              <path d="M0,130 C190,140 260,195 323,215" className="gw-line" style={{animationDelay: '0.15s'}} />
              <path d="M0,248 C190,238 270,268 323,278" className="gw-line" style={{animationDelay: '0.3s'}} />
              <path d="M0,365 C190,335 270,332 323,332" className="gw-line" style={{animationDelay: '0.45s'}} />
              <path d="M0,495 C190,435 270,392 323,388" className="gw-line" style={{animationDelay: '0.6s'}} />
              <path d="M0,605 C190,535 260,448 323,442" className="gw-line" style={{animationDelay: '0.75s'}} />
              {/* Right lines - from gateway right edge to destination boxes left edge */}
              <path d="M533,160 C680,140 740,0 810,20" className="gw-line" style={{animationDelay: '0.1s'}} />
              <path d="M533,250 C680,200 760,95 810,95" className="gw-line" style={{animationDelay: '0.2s'}} />
              <path d="M533,290 C680,310 740,230 810,200" className="gw-line" style={{animationDelay: '0.4s'}} />
              <path d="M533,320 C680,330 740,350 810,350" className="gw-line" style={{animationDelay: '0.4s'}} />
              <path d="M533,380 C680,420 740,480 810,480" className="gw-line" style={{animationDelay: '0.6s'}} />
              <path d="M533,450 C680,520 740,580 810,590" className="gw-line" style={{animationDelay: '0.8s'}} />
            </svg>
            {/* Left side - AI Sources */}
            <div className="gw-left-side">
              <div className="gw-source-row">
                <div className="gw-source-item">
                  <img src={chatIcon} alt="AI CRAWLs" className="gw-source-icon invert" />
                  <span className="gw-source-label">AI CRAWLs</span>
                </div>
                <span className="gw-attack-label">Tool Manipulation</span>
              </div>
              <div className="gw-source-row">
                <div className="gw-source-item">
                  <img src={mcpIcon} alt="MCP Client" className="gw-source-icon invert" />
                  <span className="gw-source-label">MCP Client</span>
                </div>
                <span className="gw-attack-label" style={{ transform: 'translateY(-4px)' }}>Data Loss</span>
              </div>
              <div className="gw-source-row">
                <div className="gw-source-item">
                  <img src={llmIcon} alt="LLMs" className="gw-source-icon invert" />
                  <span className="gw-source-label">LLMs</span>
                </div>
                <span className="gw-attack-label" style={{ transform: 'translateY(-22px)' }}>Hijacking</span>
              </div>
              <div className="gw-source-row">
                <div className="gw-source-item">
                  <img src={agentIcon} alt="AI Agents" className="gw-source-icon invert" />
                  <span className="gw-source-label">AI Agents</span>
                </div>
                <span className="gw-attack-label">DoS</span>
              </div>
              <div className="gw-source-row">
                <div className="gw-source-item">
                  <img src={aiAppsIcon} alt="AI Apps" className="gw-source-icon invert" />
                  <span className="gw-source-label">AI Apps</span>
                </div>
                <span className="gw-attack-label">Jailbreak</span>
              </div>
              <div className="gw-source-row">
                <div className="gw-source-item">
                  <img src={apiIcon} alt="APIs" className="gw-source-icon invert" />
                  <span className="gw-source-label">APIs</span>
                </div>
                <span className="gw-attack-label">Prompt Injection</span>
              </div>
            </div>

            {/* Center - Gateway Card */}
            <div className="gw-center">
              <div className="gw-neon-border"></div>
              <div className="gw-neon-glow"></div>
              <div className="gw-gateway-card">
                <img src={runtimeImg} alt="Shield" className="gw-shield-icon" />
                <h3 className="gw-gateway-title">Runtime Shield Gateway</h3>
                <div className="gw-feature-wrapper">
                  <div className="gw-gateway-feature" onClick={() => navigate('/runtime-shield')}>Visibility</div>
                  <p className="gw-feature-desc">Discover and monitor all AI traffic across your network in real time.</p>
                </div>
                <div className="gw-feature-wrapper">
                  <div className="gw-gateway-feature" onClick={() => navigate('/runtime-shield')}>Prevention</div>
                  <p className="gw-feature-desc">Block prompt attacks, unauthorized access, and data exfiltration automatically.</p>
                </div>
                <div className="gw-feature-wrapper">
                  <div className="gw-gateway-feature" onClick={() => navigate('/runtime-shield')}>Remediation</div>
                  <p className="gw-feature-desc">Self-healing response with automated governance and policy enforcement.</p>
                </div>
              </div>
            </div>

            {/* Right side - Risks & Destinations */}
            <div className="gw-right-side">
              <div className="gw-risk-labels">
                <span className="gw-attack-label gw-risk-pink" style={{ transform: 'translateY(8px)' }}>Access Compromise</span>
                <span className="gw-attack-label gw-risk-pink" style={{ transform: 'translateY(-4px)' }}>Behavioral Risk</span>
                <span className="gw-attack-label gw-risk-pink">Poisoning</span>
                <span className="gw-attack-label gw-risk-pink">Ransom</span>
                <span className="gw-attack-label gw-risk-pink">Data Leakage</span>
                <span className="gw-attack-label gw-risk-pink" style={{ transform: 'translateY(-8px)' }}>Prompt Leakage</span>
              </div>
              <div className="gw-destinations">
                <div className="gw-dest-section">
                  <span className="gw-dest-title">Frontier Models</span>
                  <div className="gw-dest-icons">
                    <img src={gptIcon} alt="OpenAI" className="gw-dest-icon invert" />
                    <img src={claudeIcon} alt="Claude" className="gw-dest-icon" />
                    <img src={geminiIcon} alt="Gemini" className="gw-dest-icon" />
                    <img src={deepseekIcon} alt="DeepSeek" className="gw-dest-icon" />
                    <img src={metaIcon} alt="Meta" className="gw-dest-icon" />
                    <img src={huggingFaceIcon} alt="HuggingFace" className="gw-dest-icon" />
                    <img src={mistralIcon} alt="Mistral" className="gw-dest-icon" />
                    <img src={cohereIcon} alt="Cohere" className="gw-dest-icon" />
                  </div>
                </div>
                <div className="gw-dest-section">
                  <span className="gw-dest-title">Homegrown Tools</span>
                  <div className="gw-dest-icons grid-2x2">
                    <div className="gw-dest-labeled-icon">
                      <img src={aiAppsIcon} alt="AI Apps" className="gw-dest-icon invert" />
                      <span>AI Apps</span>
                    </div>
                    <div className="gw-dest-labeled-icon">
                      <img src={agentIcon} alt="AI Agents" className="gw-dest-icon invert" />
                      <span>AI Agents</span>
                    </div>
                    <div className="gw-dest-labeled-icon">
                      <img src={mcpIcon} alt="MCP" className="gw-dest-icon invert" />
                      <span>MCP</span>
                    </div>
                    <div className="gw-dest-labeled-icon">
                      <img src={llmIcon} alt="Model" className="gw-dest-icon invert" />
                      <span>Model</span>
                    </div>
                  </div>
                </div>
                <div className="gw-dest-section">
                  <span className="gw-dest-title">Dev Tools</span>
                  <div className="gw-dest-icons grid-2x2">
                    <div className="gw-dest-labeled-icon">
                      <img src={bedrockIcon} alt="Bedrock" className="gw-dest-icon" />
                      <span>Bedrock</span>
                    </div>
                    <div className="gw-dest-labeled-icon">
                      <img src={copilotIcon} alt="Code Writer" className="gw-dest-icon invert" />
                      <span>Code Writer</span>
                    </div>
                    <div className="gw-dest-labeled-icon">
                      <img src={databaseBlueIcon} alt="RAG DB" className="gw-dest-icon" />
                      <span>RAG DB</span>
                    </div>
                    <div className="gw-dest-labeled-icon">
                      <img src={huggingFaceIcon} alt="Open Model" className="gw-dest-icon" />
                      <span>Open Model</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <a href="/runtime-shield" className="section-cta-link" onClick={(e) => { e.preventDefault(); navigate('/runtime-shield'); }}>Also See Runtime Shield API in Action ›</a>
      </section>

      <section className="section-dark" ref={sectionDarkRef}>
        <div className="section-dark-divider" ref={dividerRef}>
          <div className="divider-red"></div>
          <div className="divider-blue"></div>
        </div>
        <div className="section-dark-product">
          <img src={purpleteamImg} alt="Purple Shield" className="section-dark-shield" />
          <span className="section-dark-label">Bltz Purple Team</span>
        </div>
        <h2 id="purple-team" className="section-dark-title">Don't Stop At Red Team, Remediate with Purple Team</h2>
        <p className="section-dark-desc">Bltz Purple Team provides continuous, agentic AI risk testing and remediation, combining offensive (red team) and defensive (blue team) AI agents.</p>

        <button className="section-learn-more-btn" onClick={() => { navigate('/purple-team'); window.scrollTo(0, 0); }}>Learn More</button>

        <div className="pt-cards" ref={ptCardsRef}>
          <div className="pt-card pt-card-red" onClick={() => navigate('/purple-team#section-red-team')}>
            <img src={redBgImg} alt="" className="pt-card-still" />
            <img src={redGif} alt="" className="pt-card-gif" />
            <div className="pt-card-content">
              <h3 className="pt-card-label">Agentic Red Teaming</h3>
              <h4 className="pt-card-title">Relentless Attacker Agent Finding Domain Specific Vulnerabilities in Your AI Systems</h4>
              <button className="pt-card-btn" onClick={() => navigate('/purple-team#section-red-team')}>Learn More &rarr;</button>
            </div>
          </div>
          <div className="pt-card pt-card-blue" onClick={() => navigate('/purple-team#section-blue-team')}>
            <img src={blueBgImg} alt="" className="pt-card-still" />
            <img src={blueGif} alt="" className="pt-card-gif" />
            <div className="pt-card-content">
              <h3 className="pt-card-label">Agentic Blue Teaming</h3>
              <h4 className="pt-card-title">Self-Learning Defender Agent Mitigating Threats in Real Time</h4>
              <button className="pt-card-btn" onClick={() => navigate('/purple-team#section-blue-team')}>Learn More &rarr;</button>
            </div>
          </div>
          <div className="pt-card pt-card-how" onClick={() => navigate('/purple-team#section-how-it-works')}>
            <img src={howBgImg} alt="" className="pt-card-still" />
            <img src={howGif} alt="" className="pt-card-gif" />
            <div className="pt-card-content">
              <h3 className="pt-card-label">Why Defensive AI Security?</h3>
              <h4 className="pt-card-title">See how with right agentic strategies, you can protect your AI systems effectively</h4>
              <button className="pt-card-btn" onClick={() => navigate('/purple-team#section-how-it-works')}>Learn More &rarr;</button>
            </div>
          </div>
        </div>

        <div className="pt-details">
          <div className="pt-details-col pt-details-red">
            <div className="pt-details-label-row pt-details-label-row-red">
              <h3 className="pt-details-label">Agentic Red Team</h3>
            </div>
            <div className="pt-details-box pt-details-box-red" onClick={() => navigate('/purple-team#section-red-team')} style={{ cursor: 'pointer' }}>
              <h4 className="pt-details-agent-heading pt-details-agent-red">Offensive Agent</h4>
              <ul className="pt-details-list">
                <li>Simulates real-world attacks, not static prompt tests</li>
                <li>Executes multi-step adversarial attack chains</li>
                <li>Uncovers vulnerabilities generic tools miss</li>
              </ul>
              <button className="pt-see-action pt-see-action-red" onClick={() => navigate('/purple-team#section-red-team')}>See it in Action &rarr;</button>
            </div>
          </div>
          <div className="pt-details-shield">
            <svg className="pt-details-shield-curved-text-top" width="220" height="60" viewBox="0 0 220 60" overflow="visible">
              <defs>
                <path id="curvedTextPathTop" d="M 5,55 A 110,110 0 0,1 215,55" fill="none" />
              </defs>
              <text fill="rgba(160, 100, 255, 0.55)" fontSize="11" fontFamily="Inter, sans-serif" fontWeight="600" letterSpacing="3">
                <textPath href="#curvedTextPathTop" startOffset="50%" textAnchor="middle">BLTZ PURPLE TEAM</textPath>
              </text>
            </svg>
            <svg className="pt-details-shield-orbit" width="180" height="180" viewBox="0 0 180 180">
              <defs>
                <marker id="arrow-red" markerWidth="12" markerHeight="10" refX="6" refY="5" orient="auto">
                  <polygon points="12,0 0,5 12,10" fill="rgba(255, 59, 92, 0.25)" />
                </marker>
                <marker id="arrow-blue" markerWidth="12" markerHeight="10" refX="6" refY="5" orient="auto">
                  <polygon points="12,0 0,5 12,10" fill="rgba(80, 100, 255, 0.25)" />
                </marker>
              </defs>
              <path d="M 90,3 A 87,87 0 0,0 78,176" fill="none" stroke="rgba(255, 59, 92, 0.25)" strokeWidth="1" strokeLinecap="round" markerStart="url(#arrow-red)" />
              <path d="M 90,177 A 87,87 0 0,0 102,4" fill="none" stroke="rgba(80, 100, 255, 0.25)" strokeWidth="1" strokeLinecap="round" markerStart="url(#arrow-blue)" />
            </svg>
            <img src={purpleteamImg} alt="Purple Shield" className="pt-details-shield-img" />
            <svg className="pt-details-shield-curved-text" width="220" height="60" viewBox="0 0 220 60" overflow="visible">
              <defs>
                <path id="curvedTextPath" d="M 5,5 A 110,110 0 0,0 215,5" fill="none" />
              </defs>
              <text fill="rgba(255,255,255,0.55)" fontSize="11" fontFamily="Inter, sans-serif" fontWeight="600" letterSpacing="3">
                <textPath href="#curvedTextPath" startOffset="50%" textAnchor="middle">CLOSED LOOP DEFENSE</textPath>
              </text>
            </svg>
          </div>
          <div className="pt-details-col pt-details-blue">
            <div className="pt-details-label-row pt-details-label-row-blue">
              <h3 className="pt-details-label">Agentic Blue Team</h3>
            </div>
            <div className="pt-details-box pt-details-box-blue" onClick={() => navigate('/purple-team#section-blue-team')} style={{ cursor: 'pointer' }}>
              <h4 className="pt-details-agent-heading pt-details-agent-blue">Defensive Agent</h4>
              <ul className="pt-details-list">
                <li>Detects and blocks threats in real time</li>
                <li>Automatically remediates discovered risks</li>
                <li>Removes manual fix cycles and reduces cost</li>
              </ul>
              <button className="pt-see-action pt-see-action-blue" onClick={() => navigate('/purple-team#section-blue-team')}>See it in Action &rarr;</button>
            </div>
          </div>
        </div>

      </section>

      <section className="section-workspace" id="workspace">
        <h2 className="workspace-title">AI Governance Automation</h2>
        <p className="workspace-desc">AI governance automation platform that provides organizations with visibility, policy enforcement, automation, and compliance reporting for how employees and teams use AI tools.</p>
        <div className="workspace-product">
          <img src={blueShieldImg} alt="Blue Shield" className="workspace-shield" />
          <span className="workspace-label">Bltz Workspace</span>
        </div>
        <div className="workspace-cards">
          <div className="workspace-card workspace-card-img-left">
            <img src={workspaceVisibilityImg} alt="AI Asset Visibility" className="workspace-card-img" />
            <div className="workspace-card-content">
              <h3 className="workspace-card-label">See Every AI System</h3>
              <h4 className="workspace-card-title">Agents, copilots, and shadow AI — mapped and monitored in real time.</h4>
              <button className="workspace-card-btn" onClick={() => navigate('/ai-governance#section-visibility')}>Learn More &rarr;</button>
            </div>
          </div>

          <div className="workspace-card workspace-card-img-right">
            <div className="workspace-card-content">
              <h3 className="workspace-card-label">Automate AI Governance</h3>
              <h4 className="workspace-card-title">Enforce policies across prompts, data, and usage — automatically.</h4>
              <button className="workspace-card-btn" onClick={() => navigate('/ai-governance#section-governance')}>Learn More &rarr;</button>
            </div>
            <img src={aiGovernancePolicyImg} alt="AI Governance Automation" className="workspace-card-img" />
          </div>

          <div className="workspace-card workspace-card-img-left">
            <img src={isoChartImg} alt="Compliance Frameworks" className="workspace-card-img" />
            <div className="workspace-card-content">
              <h3 className="workspace-card-label">Built for Enterprise Compliance</h3>
              <h4 className="workspace-card-title">Aligned to NIST, ISO 42001, and SOC 2 — out of the box.</h4>
              <button className="workspace-card-btn" onClick={() => navigate('/ai-governance#section-compliance')}>Learn More &rarr;</button>
            </div>
          </div>

          <div className="workspace-card workspace-card-img-right">
            <div className="workspace-card-content">
              <h3 className="workspace-card-label">Block Unsanctioned AI Tools</h3>
              <h4 className="workspace-card-title">Detect, control, and stop unauthorized AI applications before data is exposed.</h4>
              <button className="workspace-card-btn" onClick={() => navigate('/ai-governance#section-shadow-ai')}>Learn More &rarr;</button>
            </div>
            <img src={openclawWarningImg} alt="Block AI Tools" className="workspace-card-img" />
          </div>
        </div>
      </section>
    </>
  );
};

export default Main;
